import React from 'react';
import { Info, Scale, Calculator, Award, AlertTriangle, BarChart2, Activity } from 'lucide-react';

/**
 * Компонент с информацией об индексе массы тела
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё об индексе массы тела: что это и как использовать
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Scale className="w-5 h-5 text-emerald-500" />
          Что такое индекс массы тела (ИМТ)?
        </h3>
        <p>
          <strong>Индекс массы тела (ИМТ)</strong> — это числовой показатель, позволяющий оценить соответствие 
          массы человека его росту и определить, является ли масса недостаточной, нормальной или избыточной. 
          ИМТ рассчитывается по формуле: <em>ИМТ = вес (кг) / рост² (м²)</em>.
        </p>
        <p>
          Этот показатель был предложен бельгийским социологом и статистиком Адольфом Кетле в 1869 году. 
          В современной медицине ИМТ используется как один из основных критериев для диагностики ожирения 
          и оценки риска развития заболеваний, связанных с избыточным весом.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Классификация ИМТ по данным ВОЗ и Минздрава РФ
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-emerald-100 dark:bg-emerald-900/30">
                <th className="border border-emerald-200 dark:border-emerald-800 px-4 py-2 text-left">Категория</th>
                <th className="border border-emerald-200 dark:border-emerald-800 px-4 py-2 text-left">Значение ИМТ (кг/м²)</th>
                <th className="border border-emerald-200 dark:border-emerald-800 px-4 py-2 text-left">Риск для здоровья</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Выраженный дефицит массы тела</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">менее 16</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Высокий (истощение, анорексия)</td>
              </tr>
              <tr className="bg-emerald-50 dark:bg-emerald-900/10">
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Недостаточная масса тела</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">16 – 18,5</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Повышенный</td>
              </tr>
              <tr>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Нормальная масса тела</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">18,5 – 25</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Минимальный</td>
              </tr>
              <tr className="bg-emerald-50 dark:bg-emerald-900/10">
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Избыточная масса тела (предожирение)</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">25 – 30</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Повышенный</td>
              </tr>
              <tr>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Ожирение I степени</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">30 – 35</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Высокий</td>
              </tr>
              <tr className="bg-emerald-50 dark:bg-emerald-900/10">
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Ожирение II степени</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">35 – 40</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Очень высокий</td>
              </tr>
              <tr>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Ожирение III степени</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">более 40</td>
                <td className="border border-emerald-200 dark:border-emerald-800 px-4 py-2">Крайне высокий</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          Эта классификация рекомендована Всемирной организацией здравоохранения (ВОЗ) и используется Министерством 
          здравоохранения РФ для оценки рисков, связанных с избыточным весом и ожирением. По данным Росстата, 
          по состоянию на 2023 год, около 57% взрослого населения России имеют избыточную массу тела, 
          а 23% страдают от ожирения.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Статистика и тенденции в России</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            По данным Национального медицинского исследовательского центра профилактической медицины Минздрава России, 
            распространенность ожирения в России за последние 15 лет выросла более чем в 2 раза. Особенно беспокоит рост 
            доли детей и подростков с избыточным весом, которая составляет сейчас около 19-20%.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Award className="w-5 h-5 text-emerald-500" />
          Достоинства и преимущества ИМТ
        </h3>
        <ul>
          <li>Простота расчета и доступность</li>
          <li>Универсальность применения для взрослых людей</li>
          <li>Международное признание и стандартизация</li>
          <li>Хорошая корреляция с общим содержанием жира в организме</li>
          <li>Возможность оценки динамики изменения веса</li>
          <li>Эффективный инструмент скрининга для выявления групп риска</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Ограничения и недостатки ИМТ
        </h3>
        <ul>
          <li>Не учитывает состав тела (соотношение мышечной и жировой ткани)</li>
          <li>Может давать некорректные результаты для спортсменов, пожилых людей, беременных женщин</li>
          <li>Не учитывает распределение жира в организме (абдоминальное ожирение имеет более высокий риск)</li>
          <li>Требует дополнительных параметров для полной оценки здоровья</li>
          <li>Для детей и подростков используются специальные процентильные таблицы, а не стандартные категории</li>
          <li>Могут быть различия в оптимальных значениях для разных этнических групп</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важное замечание
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            ИМТ следует рассматривать как один из показателей здоровья, но не как единственный критерий. 
            Для более полной оценки состояния организма рекомендуется учитывать дополнительные параметры, 
            такие как объем талии, состав тела, наличие сопутствующих заболеваний и семейный анамнез.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-emerald-500" />
          Дополнительные методы оценки состава тела
        </h3>
        <p>
          В дополнение к ИМТ для более точной оценки состава тела могут использоваться:
        </p>
        <ul>
          <li><strong>Измерение окружности талии</strong> — показатель абдоминального ожирения. 
          По рекомендациям Минздрава РФ, риск для здоровья повышен, если окружность талии превышает 
          94 см у мужчин и 80 см у женщин.</li>
          
          <li><strong>Соотношение окружности талии к окружности бедер (ОТБ)</strong> — показатель 
          распределения жира. Значения выше 0,9 для мужчин и 0,85 для женщин связаны с повышенным риском.</li>
          
          <li><strong>Биоимпедансный анализ</strong> — метод определения состава тела путем измерения 
          электрического сопротивления тканей.</li>
          
          <li><strong>Калиперометрия</strong> — измерение толщины кожно-жировых складок.</li>
          
          <li><strong>Денситометрия (DXA)</strong> — двухэнергетическая рентгеновская абсорбциометрия, 
          позволяющая точно определить количество костной, мышечной и жировой ткани.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-500" />
          Рекомендации для достижения и поддержания здорового веса
        </h3>
        <p>
          Российское общество профилактики неинфекционных заболеваний и Национальная Ассоциация Диетологов 
          и Нутрициологов рекомендуют:
        </p>
        <ol>
          <li>
            <strong>Сбалансированное питание</strong> — придерживайтесь принципов здорового питания, включающего 
            достаточное количество овощей, фруктов, цельнозерновых продуктов, белка и ненасыщенных жиров.
          </li>
          <li>
            <strong>Регулярная физическая активность</strong> — минимум 150 минут умеренной аэробной активности
            или 75 минут интенсивной аэробной активности в неделю, плюс минимум 2 дня в неделю силовых тренировок.
          </li>
          <li>
            <strong>Режим питания</strong> — регулярное питание в одно и то же время, без пропуска приемов пищи.
          </li>
          <li>
            <strong>Контроль размера порций</strong> — соблюдение рекомендованных объемов порций.
          </li>
          <li>
            <strong>Достаточный сон</strong> — 7-8 часов полноценного сна ежедневно.
          </li>
          <li>
            <strong>Управление стрессом</strong> — применение техник релаксации, медитации, дыхательных упражнений.
          </li>
          <li>
            <strong>Регулярный мониторинг веса</strong> — еженедельный или ежемесячный контроль веса и ИМТ.
          </li>
        </ol>

        <h3>Для кого важен контроль ИМТ?</h3>
        <p>
          Особенно важно следить за индексом массы тела следующим группам людей:
        </p>
        <ul>
          <li>Лицам с семейной предрасположенностью к ожирению и связанным с ним заболеваниям</li>
          <li>Пациентам с сердечно-сосудистыми заболеваниями или факторами риска их развития</li>
          <li>Людям с нарушениями углеводного обмена (диабет, предиабет)</li>
          <li>Пациентам с заболеваниями опорно-двигательного аппарата</li>
          <li>Людям старше 40 лет</li>
          <li>Лицам, имеющим профессии, требующие определенных физических параметров</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор ИМТ поможет вам оценить соотношение веса и роста, определить категорию и получить
          персональные рекомендации для достижения и поддержания здорового веса!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;